// src/pages/GpmHistory.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { fmtInt, fmtGBP, fmtDay } from '../utils/format';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';

function parseDate(d){ return d ? new Date(d) : null; }
const KNOWN_SITES = ['main', 'guildford', 'northern'];

// Normalize one history row -> no pricing fields for GPM
function normalizeRow(r = {}) {
  const rawEvent = String(r.event || '').trim();
  let site = String(r.site || '').trim().toLowerCase();
  let event = rawEvent;

  const amount = Number(r.amount ?? 0);
  const unitPrice = Number(r.unitPrice ?? 0);
  const totalPrice = r.totalPrice != null ? Number(r.totalPrice) : amount * unitPrice;

  if (!site) {
    const m = rawEvent.toLowerCase().match(/^(delivery|withdrawal)\s+(?:from|to)\s+([a-z0-9 \-]+)$/i);
    if (m) {
      const candidate = m[2].trim().toLowerCase();
      const matched = KNOWN_SITES.find(s => candidate.includes(s));
      if (matched) {
        site = matched;
        event = m[1][0].toUpperCase() + m[1].slice(1).toLowerCase();
      }
    }
  }
  if (!site && rawEvent) {
    const lower = rawEvent.toLowerCase();
    const found = KNOWN_SITES.find(s => lower.includes(s));
    if (found) site = found;
  }

  const lowerEvent = event.toLowerCase();
  if (lowerEvent.startsWith('delivery')) event = 'Delivery';
  else if (lowerEvent.startsWith('withdrawal')) event = 'Withdrawal';
  else event = event || 'Other';

  if (!site) site = '—';

  return {
    id: r.id,
    date: r.date ?? '',
    event,
    team: r.team ?? '',
    item: r.item ?? '',
    amount,
    unitPrice,
    totalPrice,
    site
  };
}

export default function GpmHistory() {
  const { api } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        // GPM endpoint (no prices)
        const data = await api.get('/gpm/history').then(r => r.data).catch(() => []);
        const list = Array.isArray(data) ? data : [];
        const normalized = list.map(normalizeRow);
        if (alive) setRows(normalized);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e?.message || 'Failed to load history');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  // Option lists
  const events = useMemo(() => {
    const u = new Set(rows.map(r => r.event || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);
  const teams = useMemo(() => {
    const u = new Set(rows.map(r => r.team || '—'));
    return ['all', ...Array.from(u).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);
  const sites = useMemo(() => {
    const u = new Set(rows.map(r => r.site || '—'));
    const rest = Array.from(u).filter(s => !KNOWN_SITES.includes(s));
    return ['all', ...KNOWN_SITES.filter(s => u.has(s)), ...rest.filter(Boolean).sort((a,b)=>a.localeCompare(b))];
  }, [rows]);

  // Filter + sort (newest first)
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const fromDate = parseDate(from);
    const toDate = parseDate(to);

    return rows.filter(r => {
      if (fromDate || toDate) {
        const rd = parseDate(r.date);
        if (!rd) return false;
        if (fromDate && rd < fromDate) return false;
        if (toDate) {
          const end = new Date(toDate);
          end.setHours(23,59,59,999);
          if (rd > end) return false;
        }
      }
      if (eventFilter !== 'all' && (r.event || '—') !== eventFilter) return false;
      if (teamFilter !== 'all' && (r.team || '—') !== teamFilter) return false;
      if (siteFilter !== 'all' && (r.site || '—') !== siteFilter) return false;

      if (ql) {
        const hay = [
          r.item, r.event, r.team, r.site,
          String(r.amount ?? ''), String(r.unitPrice ?? ''), String(r.totalPrice ?? ''), r.date
        ].join(' ').toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    }).sort((a, b) => {
      const ad = a.date || '';
      const bd = b.date || '';
      return ad < bd ? 1 : ad > bd ? -1 : 0;
    });
  }, [rows, q, eventFilter, teamFilter, siteFilter, from, to]);

  useEffect(() => { setPage(1); }, [q, eventFilter, teamFilter, siteFilter, from, to]);

  // Summary (no currency)
  const summary = useMemo(() => {
    const count = filtered.length;
    const totalUnits = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalValue = filtered.reduce((s, r) => s + (Number(r.totalPrice || 0)), 0);
    return { count, totalUnits, totalValue };
  }, [filtered]);

  // Pagination slice
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page-1)*pageSize, page*pageSize);

  // CSV export (no price columns)
  function exportCSV() {
    const header = ['date','event','team','item','amount','unitPrice','totalPrice','site'];
    const lines = [header.join(',')];
    filtered.forEach(r => {
      const row = [
        r.date ?? '',
        (r.event ?? '').replace(/,/g,';'),
        (r.team ?? '').replace(/,/g,';'),
        (r.item ?? '').replace(/,/g,';'),
        Number(r.amount ?? 0),
        Number(r.unitPrice ?? 0),
        Number(r.totalPrice ?? 0),
        (r.site ?? '').replace(/,/g,';')
      ];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `gpm_history_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Quick-range helpers
function setRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  const iso = d => d.toISOString().slice(0, 10);
  setFrom(iso(start));
  setTo(iso(end));
}

function clearDates() {
  setFrom('');
  setTo('');
}

function resetFilters() {
  setQ('');
  setEventFilter('all');
  setTeamFilter('all');
  setSiteFilter('all');
  clearDates();
}

  const evtClass = (e) => e === 'Delivery' ? 'badge evt delivery' : e === 'Withdrawal' ? 'badge evt withdrawal' : 'badge';
  const filtersActive = q || eventFilter !== 'all' || teamFilter !== 'all' || siteFilter !== 'all' || from || to;

  return (
    <div className="container">
      <PageHeader
        eyebrow="GPM"
        title="History"
        actions={
          <button className="btn secondary" onClick={exportCSV}>
            <Icon name="download" size={15} /> Export CSV
          </button>
        }
      />

      {error && <div className="page-alerts"><div className="alert error">{error}</div></div>}

      <div className="metrics">
        <div className="metric">
          <div className="metric-label">Entries</div>
          <div className="metric-value">{fmtInt(summary.count)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Units</div>
          <div className="metric-value">{fmtInt(summary.totalUnits)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Value</div>
          <div className="metric-value">{fmtGBP(summary.totalValue)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-16">
        <div className="filters-grid">
          <label className="input-label">Search
            <input
              className="input"
              placeholder="Item, event, team, site, amount"
              value={q}
              onChange={e=>setQ(e.target.value)}
            />
          </label>

          <label className="input-label">Event
            <select className="input" value={eventFilter} onChange={e=>setEventFilter(e.target.value)}>
              {events.map(opt => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All events' : opt}
                </option>
              ))}
            </select>
          </label>

          <label className="input-label">Team
            <select className="input" value={teamFilter} onChange={e=>setTeamFilter(e.target.value)}>
              {teams.map(opt => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All teams' : opt === '—' ? 'No team' : opt}
                </option>
              ))}
            </select>
          </label>

          <label className="input-label">Site
            <select className="input" value={siteFilter} onChange={e=>setSiteFilter(e.target.value)}>
              {sites.map(opt => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All sites' : opt}
                </option>
              ))}
            </select>
          </label>

          <label className="input-label">From
            <input className="input" type="date" value={from} onChange={e=>setFrom(e.target.value)} />
          </label>

          <label className="input-label">To
            <input className="input" type="date" value={to} onChange={e=>setTo(e.target.value)} />
          </label>
        </div>

        <div className="toolbar mt-16">
          <button className="btn small secondary" onClick={()=>setRange(1)}>Today</button>
          <button className="btn small secondary" onClick={()=>setRange(7)}>Last 7 days</button>
          <button className="btn small secondary" onClick={()=>setRange(30)}>Last 30 days</button>
          <button className="btn small ghost" onClick={clearDates} disabled={!from && !to}>Clear dates</button>
          <span className="spacer" />
          <button className="btn small secondary destructive" onClick={resetFilters} disabled={!filtersActive}>Reset all</button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty">Loading</div>
        ) : filtered.length === 0 ? (
          <div className="empty">No matching history.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th>Team</th>
                    <th>Item</th>
                    <th className="num">Amount</th>
                    <th className="num">Unit price</th>
                    <th className="num">Total</th>
                    <th>Site</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => (
                    <tr key={r.id || `${r.item}-${r.date}-${i}`}>
                      <td className="nowrap">{fmtDay(r.date)}</td>
                      <td><span className={evtClass(r.event)}>{r.event}</span></td>
                      <td className="cell-muted">{r.team || '—'}</td>
                      <td>{r.item || '—'}</td>
                      <td className="num">{fmtInt(r.amount)}</td>
                      <td className="num">{fmtGBP(r.unitPrice)}</td>
                      <td className="num">{fmtGBP(r.totalPrice)}</td>
                      <td><span className="tag">{r.site}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pager">
              <span className="pager-info">
                Page {page} of {totalPages}, showing {pageRows.length} of {filtered.length}
              </span>
              <div className="pager-buttons">
                <button className="btn secondary" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Previous</button>
                <button className="btn secondary" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
